'use client';

import { useNavigate, useParams } from 'react-router-dom';
import { useState } from 'react';
import ProfileHolidayRequest from './components/ProfileHolidayRequest';
import ProfileDescription from './components/ProfileDescription';
import { useProfileDetails } from '../../hooks/useProfileDetails';
import { useAuth } from '../../hooks/useAuth';
import { Avatar } from '../../ui/component/avatar';
import { ChangePasswordForm } from './components/ChangePasswordForm';
import { CalendarConnections } from '../Settings/components/integrations/CalendarConnections';
import { useIntegrationCallbackToast } from '../Settings/components/integrations/api';
import { PersonDataControls } from './components/PersonDataControls';
import { useCurrentUserRole } from '../../hooks/useCurrentUserRole';
import { hasRoleLevel } from '../../utils/roles';

// Helper function to conditionally apply class names
function classNames(...classes: (string | boolean | undefined)[]): string {
  return classes.filter(Boolean).join(' ');
}

export function ProfileRoute() {
  const { id } = useParams();
  const { user } = useAuth();
  const profileId = id || user?.id;
  const { data: profileData, isLoading: profileLoading } =
    useProfileDetails(profileId);
  const { level } = useCurrentUserRole();
  const isAdmin = hasRoleLevel(level, 'admin');
  const navigate = useNavigate();

  // State to manage the active view in the secondary navigation. Calendar
  // OAuth callbacks come back to /profile?tab=calendar.
  const [activeView, setActiveView] = useState(() =>
    new URLSearchParams(window.location.search).get('tab') === 'calendar'
      ? 'calendar'
      : 'profile',
  );
  useIntegrationCallbackToast();

  // Define items for the secondary navigation bar
  // The password form changes the signed-in user's password, so only offer
  // it on your own profile.
  const isOwnProfile = !id || id === user?.id;
  const secondaryNavigationItems = [
    { name: 'Profile', slug: 'profile' },
    { name: 'Holiday Requests', slug: 'holiday-requests' },
    ...(isOwnProfile ? [{ name: 'Change Password', slug: 'password' }] : []),
    // Personal calendar sync is open to every role, on your own profile.
    ...(isOwnProfile ? [{ name: 'Calendar', slug: 'calendar' }] : []),
    ...(isOwnProfile || isAdmin ? [{ name: isOwnProfile ? 'Your Data' : 'Data & Privacy', slug: 'data' }] : []),
    // Add other relevant settings sections here if needed
  ];

  // Get initials for avatar fallback
  const getInitials = () => {
    if (!profileData) return '';
    const names = profileData.full_name?.split(' ') || [];
    return names.length > 1
      ? (names[0][0] + names[names.length - 1][0]).toUpperCase()
      : names[0]?.[0]?.toUpperCase() || '';
  };

  if (profileLoading) {
    // Basic loading state, consider a more sophisticated skeleton loader for better UX
    return (
      <div className="bg-white-100 flex min-h-screen items-center justify-center">
        <div>Loading profile...</div>
      </div>
    );
  }

  return (
    <div className="bg-white-100 min-h-screen">
      {' '}
      {/* Light mode page background */}
      <main className="mx-auto max-w-7xl py-10 lg:px-8">
        {/* Secondary navigation header */}
        <header className="border-white-200 border-b pb-5">
          <div className="px-4 sm:px-6 lg:px-0">
            <nav className="flex overflow-x-auto py-4">
              <ul className="text-white-500 flex min-w-full flex-none gap-x-6 text-sm font-semibold leading-6 sm:gap-x-8">
                {secondaryNavigationItems.map((item) => (
                  <li key={item.name}>
                    <button
                      type="button"
                      onClick={() => {
                        setActiveView(item.slug);
                      }}
                      className={classNames(
                        activeView === item.slug
                          ? 'text-red-600'
                          : 'text-white-500 hover:text-red-600',
                        'whitespace-nowrap', // Prevents text wrapping on smaller screens
                      )}
                    >
                      {item.name}
                    </button>
                  </li>
                ))}
              </ul>
            </nav>
          </div>
        </header>

        {/* Content sections based on activeView */}
        <div className="divide-white-200 divide-y">
          {/* Profile / Personal Information Section */}
          {activeView === 'profile' && (
            <div className="grid grid-cols-1 gap-x-8 gap-y-10 px-4 py-10 sm:px-6 md:grid-cols-3 lg:px-8">
              <div>
                <h2 className="text-white-900 text-base font-semibold leading-7">
                  Personal Information
                </h2>
                <p className="text-white-600 mt-1 text-sm leading-6">
                  View and manage your personal details.
                </p>
              </div>

              <div className="md:col-span-2">
                <div className="space-y-8">
                  <div className="flex items-center gap-x-6">
                    <Avatar
                      src={profileData?.safe_profile_picture_url}
                      alt={profileData?.full_name || ''}
                      initials={getInitials()}
                      className="bg-white-200 h-24 w-24 rounded-full object-cover shadow-sm"
                    />
                    <div>
                      <button
                        type="button"
                        className="text-white-900 ring-white-300 hover:bg-white-50 rounded-md bg-white px-3 py-2 text-sm font-semibold shadow-sm ring-1 ring-inset"
                      >
                        Change avatar
                      </button>
                      <p className="text-white-500 mt-2 text-xs leading-5">
                        JPG, GIF or PNG. 1MB max.
                      </p>
                    </div>
                  </div>

                  {/* Render the ProfileDescription component */}
                  {/* This component might display information or contain form fields */}
                  <ProfileDescription data={profileData} />

                  {/* If ProfileDescription is not a form and you need editable fields, */}
                  {/* you can add form elements here and a save button. */}
                  {/* Example:
                  <form>
                    ... form fields for name, email etc. ...
                    <div className="mt-8 flex">
                      <button type="submit" className="btn-primary">Save Profile</button>
                    </div>
                  </form>
                  */}
                </div>
              </div>
            </div>
          )}

          {/* Holiday Requests Section */}
          {activeView === 'holiday-requests' && (
            <div className="grid grid-cols-1 gap-x-8 gap-y-10 px-4 py-10 sm:px-6 md:grid-cols-3 lg:px-8">
              <div>
                <h2 className="text-white-900 text-base font-semibold leading-7">
                  Holiday Requests
                </h2>
                <p className="text-white-600 mt-1 text-sm leading-6">
                  View and manage your holiday requests.
                </p>
              </div>
              <div className="md:col-span-2">
                <ProfileHolidayRequest
                  userId={
                    isOwnProfile ? undefined : String(profileData?.id ?? id)
                  }
                />
              </div>
            </div>
          )}

          {/* GDPR: data export and erasure */}
          {activeView === 'data' && (isOwnProfile || isAdmin) && (
            <div className="grid grid-cols-1 gap-x-8 gap-y-10 px-4 py-10 sm:px-6 md:grid-cols-3 lg:px-8">
              <div>
                <h2 className="text-white-900 text-base font-semibold leading-7">
                  {isOwnProfile ? 'Your data' : 'Personal data'}
                </h2>
                <p className="text-white-600 mt-1 text-sm leading-6">
                  {isOwnProfile
                    ? 'Download a copy of the personal data held about you.'
                    : 'Export or erase the personal data held about this person.'}
                </p>
              </div>
              <div className="md:col-span-2">
                <PersonDataControls
                  personId={
                    isOwnProfile ? undefined : String(profileData?.id ?? id)
                  }
                  email={profileData?.email}
                  name={profileData?.full_name}
                  isOwnProfile={isOwnProfile}
                  isAdmin={isAdmin}
                  onErased={() => navigate('/people')}
                />
              </div>
            </div>
          )}

          {/* Change Password Section */}
          {activeView === 'password' && isOwnProfile && (
            <div className="grid grid-cols-1 gap-x-8 gap-y-10 px-4 py-10 sm:px-6 md:grid-cols-3 lg:px-8">
              <div>
                <h2 className="text-white-900 text-base font-semibold leading-7">
                  Change Password
                </h2>
                <p className="text-white-600 mt-1 text-sm leading-6">
                  Update your password associated with your account.
                </p>
              </div>

              <ChangePasswordForm />
            </div>
          )}

          {/* Calendar sync Section */}
          {activeView === 'calendar' && isOwnProfile && (
            <div className="grid grid-cols-1 gap-x-8 gap-y-10 px-4 py-10 sm:px-6 md:grid-cols-3 lg:px-8">
              <div>
                <h2 className="text-white-900 text-base font-semibold leading-7">
                  My calendar
                </h2>
                <p className="text-white-600 mt-1 text-sm leading-6">
                  Connect your Google or Outlook calendar. Approved leave is
                  added automatically, and removed if it is cancelled.
                </p>
              </div>
              <div className="md:col-span-2">
                <CalendarConnections returnTo="profile" />
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
